import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {
  Column,
  DrawerComponent,
  FilterKey,
  FilterOptionComponent,
  FilterOptionsRefund,
  PaginationConfig,
  PRODUCT_DRAWER_TAB,
  REFUND_TABLE_COLUMN,
  REFUND_TABLE_SEARCH,
  RefundDetails,
  RefundItems,
  RefundQueryParams,
  RefundRequestStatus,
  RefundResponse,
  RejectionPayload,
  STUDENT_DRAWER_TAB,
  STUDENT_SETTINGS_DRAWER_TAB,
  TabItem,
  TableSearch,
} from '@shared';
import { TransactionsService } from '@shared/services';
import { BreadcrumbService } from '@shared/services/breadcrumb/breadcrumb.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import * as _ from 'lodash';
import { RangeDate } from 'projects/thkee-common/src/lib/components/date-range-picker/date-range-picker.component';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  FileProps,
  getCourseModeDisplay,
  LANGUAGES,
  Logger,
  ModalComponent,
  ModalService,
  PaginationService,
  ThkeeCommonModule,
  ToastService,
  UtilsService,
} from 'thkee-common';
import { CopierComponent } from '../../../../../../thkee-common/src/lib/components/copier/copier.component';
import { ProcessingDirective } from '../../../../../../thkee-common/src/lib/directives/processing.directive';
import { TooltipDirective } from '../../../../../../thkee-common/src/lib/directives/tooltip.directive';
import { CommonPaginationComponent } from '../../../@shared/components/common-pagination/common-pagination.component';
import { DrawerComponent as DrawerComponent_1 } from '../../../@shared/components/drawer/drawer.component';
import { FilterOptionComponent as FilterOptionComponent_1 } from '../../../@shared/components/filter-option/filter-option.component';
const log = new Logger('CourseDetailsComponent');
const pageSize: number = 10;

@Component({
  selector: 'app-refund-requests',
  templateUrl: './refund-requests.component.html',
  styleUrls: ['./refund-requests.component.scss'],
  imports: [
    AngularSvgIconModule,
    FilterOptionComponent_1,
    ThkeeCommonModule,
    FormsModule,
    ProcessingDirective,
    NgTemplateOutlet,
    CommonPaginationComponent,
    CopierComponent,
    TooltipDirective,
    DrawerComponent_1,
    RouterLink,
    ModalComponent,
    ReactiveFormsModule,
    FormlyModule,
    DatePipe,
  ],
})
export class RefundRequestsComponent implements OnInit, OnDestroy {
  private breadcrumbService = inject(BreadcrumbService);
  private transactionsService = inject(TransactionsService);
  private utilsService = inject(UtilsService);
  private activatedRoute = inject(ActivatedRoute);
  private paginationService = inject(PaginationService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);

  // Create product form
  productForm = new FormGroup({});
  productFormModel: any = {};
  productFormFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'card block py-9 px-6 mb-4',
      fieldGroup: [
        {
          className: 'section-label block border-b-[2px] border-neutral-200 pb-3 mb-3',
          template: '<h5>General</h5>',
        },
        {
          key: 'title',
          type: 'input',
          wrappers: ['feedback-field'],
          defaultValue: '#SAP MM Material Management',
          props: {
            label: 'Title',
            minLength: 6,
            maxLength: 60,
            placeholder: 'e.g. Learn Blender in 60 minutes',
            disabled: true,
            tips: 'This is a tip message for course title',
          },
          validation: {
            messages: {
              minLength: "Title can't be lower than 6 characters.",
            },
          },
          expressions: {},
        },
        {
          key: 'subtitle',
          type: 'input',
          wrappers: ['feedback-field'],
          defaultValue: 'Introduction to SAP MM Material Management',
          props: {
            label: 'Subtitle',
            minLength: 6,
            maxLength: 60,
            placeholder: 'e.g. Introduction to SAP MM Material Management!',
            disabled: true,
            tips: 'This is a tip message for course sub title',
          },
          validation: {
            messages: {
              minLength: "This field can't be lower than 6 characters.",
            },
          },
          expressions: {},
        },
        {
          key: 'topics',
          type: 'select',
          defaultValue: ['uiux', 'frontend'],
          wrappers: ['feedback-field'],
          props: {
            label: 'Topics',
            placeholder: 'Choose topic',
            multiple: true,
            stayPlaceholder: true,
            disabled: true,
            tips: 'This is a tip message for course title',
            stylish: true,
            options: [
              { label: 'UI/UX', value: 'uiux' },
              { label: 'Frontend', value: 'frontend' },
              { label: 'Backend', value: 'backend' },
            ],
          },
          expressions: {},
        },
        {
          fieldGroupClassName: 'grid grid-cols-2 gap-6 border-b border-neutral-200 mb-3',
          fieldGroup: [
            {
              key: 'skill_level',
              type: 'select',
              defaultValue: 'Beginner',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Level',
                placeholder: 'Choose a level',
                stylish: true,
                options: [
                  { label: 'All', value: 'All' },
                  { label: 'Beginner', value: 'Beginner' },
                  { label: 'Intermediate', value: 'Intermediate' },
                  { label: 'Expert', value: 'Expert' },
                ],
                disabled: true,
                tips: 'This is a tip message for course title',
              },
              expressions: {},
            },
            {
              key: 'language',
              type: 'select',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Language',
                placeholder: 'Select language',
                stylish: true,
                options: _.sortBy(
                  _.entries(LANGUAGES).map(([key, value]) => ({ label: value, value: key })),
                  (lang) => lang.label
                ),
                disabled: true,
                tips: 'This is a tip message for course title',
              },
              expressions: {},
            },
          ],
        },
        {
          fieldGroupClassName: 'grid grid-cols-2 gap-6',
          fieldGroup: [
            {
              key: 'category',
              type: 'select',
              defaultValue: 'business',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Category',
                placeholder: 'Choose a category',
                stylish: true,
                disabled: true,
                tips: 'This is a tip message for course title',
                options: [
                  { label: 'Business', value: 'business' },
                  { label: 'Design', value: 'design' },
                  { label: 'Language', value: 'language' },
                ],
              },
              expressions: {},
            },
            {
              key: 'subcategory',
              type: 'select',
              defaultValue: 'graphic-design',
              wrappers: ['feedback-field'],
              props: {
                wrapAppendClass: ['border-none', '!mb-0'],
                label: 'Sub category',
                placeholder: 'Choose sub-category',
                stylish: true,
                disabled: true,
                tips: 'This is a tip message for course title',
                options: [
                  { label: 'Graphic Design', value: 'graphic-design' },
                  { label: 'Web Design', value: 'web-design' },
                  { label: 'UI/UX Design', value: 'uiux-design' },
                ],
              },
              expressions: {},
            },
          ],
        },
      ],
    },

    {
      className: 'block border-t border-neutral-100 card mb-4 pt-6 px-6',
      key: 'image',
      type: 'file',
      // wrappers: ['feedback-field'],
      props: {
        wrapAppendClass: ['border-none'],
        label: 'Image Cover',
        placeholder: 'Upload your course image here. Supported files are .jpg, .jpeg, or .png.',
        preview: true,
        previewType: 'image',
        allowedTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
        uploadType: 'dashboard',
        disabled: false,
        tips: 'This is a tip message for course title',
        acceptFileType: '.webp, .jpg, .jpeg, or .png',
      } as FileProps,
      expressions: {},
    },
    {
      className: 'block border-t border-neutral-100 card mb-4 pt-6 px-6',
      key: 'promo_video',
      type: 'file',
      // wrappers: ['feedback-field'],
      props: {
        wrapAppendClass: ['border-none'],
        label: 'Promotional Video',
        placeholder: "Upload your course's promotional video here. Maximum of 200MB.",
        preview: true,
        previewType: 'video',
        hideLabel: false,
        allowedTypes: ['video/mp4', 'video/mkv', 'video/mov'],
        uploadType: 'dashboard',
        disabled: false,
        tips: 'This is a tip message for course title',
        onUpload: (upload, field) => {
          // log.debug('upload: ', upload);
          // firstValueFrom(this.courseId$).then((courseId) => {
          //   this.store.dispatch(
          //     CourseActions.uploadStart({
          //       referenceId: `${courseId}-${field.key}`,
          //       fileName: upload.name,
          //       fileType: upload.type,
          //     })
          //   );
          // });
        },
        onProgress: (progress, field) => {
          // log.debug('progress: ', progress);
          // firstValueFrom(this.courseId$).then((courseId) => {
          //   this.store.dispatch(
          //     CourseActions.uploadProgress({
          //       referenceId: `${courseId}-${field.key}`,
          //       bytesTotal: progress.bytesTotal,
          //       bytesUploaded: progress.bytesUploaded,
          //       progressPercent: progress.progressPercent,
          //     })
          //   );
          // });
        },
        onComplete: (field) => {
          // log.debug('complete');
          // firstValueFrom(this.courseId$).then((courseId) => {
          //   this.store.dispatch(
          //     CourseActions.uploadComplete({
          //       referenceId: `${courseId}-${field.key}`,
          //     })
          //   );
          // });
        },
      } as FileProps,
      expressions: {
        // 'props.metadata': combineLatest([this.course$, this.userId$]).pipe(
        //   map(([course, userId]) => ({
        //     user: userId,
        //     course: course.id,
        //   })),
        //   distinctUntilChanged()
        // ),
        // 'props.previewUrl': this.course$.pipe(
        //   map((course) => {
        //     return course.promo_video_url;
        //   }),
        //   distinctUntilChanged()
        // ),
        // 'props.feedback': this.getFeedbackCount('promo_video'),
      },
    },
  ];

  //Product TAB
  productTabLists: TabItem[] = PRODUCT_DRAWER_TAB;

  //Student TAB
  studentTabLists: TabItem[] = STUDENT_DRAWER_TAB;

  //Student Settings TAB
  studentSettingsTabLists: TabItem[] = STUDENT_SETTINGS_DRAWER_TAB;

  // Common variables
  queryParams: RefundQueryParams = { page: 1, page_size: pageSize };
  transactionsKey: FilterKey = { key1: 'transaction_min', key2: 'transaction_max' };
  filterValues: FilterOptionsRefund = {
    status: [],
    refund_type: [],
  };
  filterOptions: FilterOptionsRefund = {
    status: [],
    refund_type: [],
  };
  searchType: TableSearch[] = REFUND_TABLE_SEARCH;
  selectedItems: TableSearch = REFUND_TABLE_SEARCH[0];
  columns: Column[] = REFUND_TABLE_COLUMN;
  isDropdown: boolean[] = [];
  refundItems: RefundItems[] = [];
  isLoading: boolean = false;
  totalOrder: number = 0;

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.queryUpdater();
    let breadcrumb: any = [
      {
        label: 'E-Commerce',
        url: '/ecommerce/dashboard',
      },
      {
        label: 'Refund Request',
        url: '',
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumb);
    this.setFilterOptions();
  }

  private queryUpdater() {
    firstValueFrom(this.activatedRoute.queryParams).then((params) => {
      this.queryParams.page = params['page'] ? +params['page'] : 1;
      this.queryParams.page_size = params['page_size'] ? +params['page_size'] : 10;
      this.getOrders();
    });
  }

  // Get data
  private getOrders(params: RefundQueryParams = {}, reload = false) {
    this.isLoading = true;
    this.subscriptions.add(
      this.transactionsService.getRefundLists(this.queryParams, reload).subscribe({
        next: (res: RefundResponse) => {
          this.refundItems = res.results.map(item => ({
            ...item,
            courseModeDisplay: getCourseModeDisplay(item.course_mode)
          }));
          this.totalOrder = res.count;
          this.paginationService.getPager(res.count, this.queryParams.page, this.queryParams.page_size);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
        },
      })
    );
  }

  filterByDate(rangeDate: RangeDate) {
    this.queryParams.date_created_before = rangeDate.end;
    this.queryParams.date_created_after = rangeDate.start;
    this.getOrders(this.queryParams);
  }

  // Common UI actions
  // Filter
  setFilterOptions() {
    this.filterOptions.status = [
      { value: 'pending', label: $localize`Pending` },
      { value: 'approved', label: $localize`Approved` },
      { value: 'refunded', label: $localize`Refunded` },
      { value: 'rejected', label: $localize`Rejected` },
      { value: 'cancelled', label: $localize`Cancelled` },
      { value: 'chargeback', label: $localize`Chargeback` },
      { value: 'reconciliation', label: $localize`Reconciliation` },
    ];
    this.filterOptions.refund_type = [
      { value: 'regular_refund', label: $localize`Regular Refund` },
      { value: 'thkee_refund', label: $localize`Thkee Refund` },
    ];
  }

  filterHander(event: { action: string; data: {} }) {
    if (event.action === 'change') {
      const queryParams = this.utilsService.convertQueryParams(event.data);
      this.queryParams = { ...this.queryParams, ...queryParams };
    }
    if (event.action === 'change-input') {
      this.queryParams = { ...this.queryParams, ...event.data };
    }
    if (event.action === 'reset') {
      this.isDropdown[1] = false;
      this.queryParams = {};
      this.clearFilter([]);
      this.getOrders(this.queryParams);
    }
  }

  clearFilter(filters: FilterOptionComponent[]) {
    if (filters) {
      filters.forEach((filter: FilterOptionComponent) => {
        filter.reset();
      });
    }
  }

  submitFilter(filters: FilterOptionComponent[]) {
    this.isDropdown[1] = false;
    this.getOrders(this.queryParams);
    // this.clearFilter(filters);
    // this.queryParams = { page: 1, page_size: pageSize };
  }

  // Search
  placeholder: string = `Search by ${this.selectedItems.title.toLowerCase()}`;
  selectItems(item: TableSearch) {
    this.queryParams = { page: 1, page_size: pageSize };
    this.selectedItems = item;
    this.placeholder = `Search by ${this.selectedItems.title.toLowerCase()}`;
  }
  search(event: any) {
    this.queryParams = Object.assign({}, this.queryParams);
    this.queryParams.search = `${this.selectedItems.value + ':' + event.target.value}`;
    this.getOrders(this.queryParams);
  }

  // Dropdown
  dropdown(type: number) {
    this.isDropdown[type] = !this.isDropdown[type];
  }

  // Show/Hide column
  toggleColumn(col: Column): void {
    col.visible = !col.visible;
  }

  // Color tag
  getStatusTagColor(status: string): string {
    if (this.utilsService.isStatus(status)) {
      return this.utilsService.getStatusColor(status);
    }
    return 'green';
  }

  // Pageination
  paginationAction(page_number: number) {
    this.queryParams = Object.assign({}, this.queryParams);
    this.queryParams.page = page_number;
    this.getOrders(this.queryParams);
  }

  paginationConfig: PaginationConfig = {
    pageCountSummary: true,
    pageSize: true,
    firstAndLast: true,
  };

  pageSizeAction(value: number) {
    this.queryParams.page_size = value;
    this.queryParams.page = 1;
    this.getOrders(this.queryParams);
  }

  formateStatusTag(status: string | undefined): string {
    if (!status) {
      throw new Error('Status is required');
    }
    const formattedStatus = status
      .toLowerCase()
      .split(/[_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return formattedStatus;
  }

  // Sidebar drawer
  @ViewChild('refundDetailsDrawer') private refundDetailsDrawer!: DrawerComponent;
  refundItem!: RefundItems;

  // Rejection Reason form
  rejectReasonForm = new FormGroup({});
  rejectReasonFormModel = { reason: 'Reason1', description: '' };
  rejectReasonFormFields: FormlyFieldConfig[] = [
    {
      key: 'reason',
      type: 'select',
      defaultValue: ['Outdated Course Material'],
      props: {
        label: $localize`Reason`,
        placeholder: $localize`Choose topic`,
        multiple: false,
        stayPlaceholder: true,
        tips: $localize`Refund reason`,
        stylish: true,
        options: [
          { value: 'Outdated Course Material', label: $localize`Outdated Course Material` },
          { value: 'Instructor Performance Issues', label: $localize`Instructor Performance Issues` },
          { value: 'Course Too Difficult', label: $localize`Course Too Difficult` },
          { value: 'Course Too Easy', label: $localize`Course Too Easy` },
          { value: 'Course Not Needed', label: $localize`Course Not Needed` },
          { value: 'Technical Issues', label: $localize`Technical Issues` },
          { value: 'Unauthorized Transaction', label: $localize`Unauthorized Transaction` },
          { value: 'Better Price Found', label: $localize`Better Price Found` },
          { value: 'Incorrect Charge', label: $localize`Incorrect Charge` },
          { value: 'Duplicate Charge', label: $localize`Duplicate Charge` },
          { value: 'Exceptional Circumstances', label: $localize`Exceptional Circumstances` },
        ],
      },
      expressions: {},
    },
    {
      key: 'description',
      type: 'textarea',
      props: {
        label: $localize`Description`,
        labelClass: 'font-bold text-black text-lg pb-2.5',
        tips: $localize`Reason description`,
        placeholder: $localize`Enter a description...`,
        required: true,
        minHeight: '100px',
        maxHeight: '150px',
      },
    },
  ];

  showDrawer(type: any, event: any, item: RefundItems) {
    event.stopPropagation();
    this.refundItem = item;
    this.refundDetailsDrawer.open();
    this.getRefundDetails();
  }
  // Get refund details data
  refundDetails!: RefundDetails;
  isLoadingDetails: boolean = false;
  private getRefundDetails() {
    this.isLoadingDetails = true;
    this.subscriptions.add(
      this.transactionsService.getRefundDetails(this.refundItem['id']).subscribe({
        next: (res: RefundDetails) => {
          this.refundDetails = {
            ...res,
            courseModeDisplay: getCourseModeDisplay(res.course_mode)
          };
          this.isLoadingDetails = false;
        },
        error: (error) => {
          this.isLoadingDetails = false;
        },
      })
    );
  }

  // Refund confirm modal
  @ViewChild('refundConfirmed') refundConfirmed!: ModalComponent;
  private refundSubscriptions = new Subscription();

  confirmRefund() {
    this.refundConfirmed.close();
    this.refundSubscriptions.add(
      this.transactionsService.refundRequestApproved(this.refundItem['id']).subscribe({
        next: (res: RefundRequestStatus) => {
          this.toastService.message({
            message: res.status,
          });
          this.getOrders(this.queryParams, true);
          this.refundDetailsDrawer.close();
        },
        error: (error) => {
          this.toastService.error({
            message: error.error.status,
          });
        },
      })
    );
  }

  // Refund
  refundConfirmation() {
    this.modalService.confirm({
      title: 'Refund Confirmation',
      message: `Are you sure want to refund this course?`,
      onConfirm: () => {
        this.refund(this.refundItem['id']);
      },
    });
  }

  private refund(refundId: string) {
    this.refundSubscriptions.add(
      this.transactionsService.refundRequestForceApproved(refundId).subscribe({
        next: (res: RefundRequestStatus) => {
          this.toastService.message({
            message: res.status,
          });
          this.getOrders(this.queryParams, true);
          this.refundDetailsDrawer.close();
        },
        error: (error) => {
          this.toastService.error({
            message: error.error.status,
          });
        },
      })
    );
  }

  // Rejection confirm modal
  @ViewChild('rejectionConfirmed') rejectionConfirmed: any;
  confirmRejection() {
    this.rejectionConfirmed.close();
    this.refundSubscriptions.add(
      this.transactionsService
        .rejectRefundRequest(this.refundItem['id'], this.rejectReasonForm.value as RejectionPayload)
        .subscribe({
          next: (res: RefundRequestStatus) => {
            this.toastService.message({
              message: res.status,
            });
            this.getOrders(this.queryParams, true);
            this.refundDetailsDrawer.close();
          },
          error: (error) => {
            this.toastService.error({
              message: error.error.status,
            });
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.refundSubscriptions.unsubscribe();
  }
}
